import NiceModal from '@ebay/nice-modal-react';
import { ActionIcon, Badge, Group, Table, Text, Tooltip } from '@mantine/core';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useDeleteRole } from '../api/deleteRole';
import { roleTypes } from '../types';

import { RoleModal } from './RoleModal';
import { RolePageAccessCell } from './RolePageAccessCell';
import { RolePermissionsCell } from './RolePermissionsCell';

import { ConfirmModal } from '@/components/Modals/Confirm';
import { getAccounts } from '@/features/accounts';
import { Departments } from '@/types';
import { COLORS } from '@/utils/colors';

const ALL_MANAGERS_COUNT = Object.values(Departments).length;

type Props = {
  role: roleTypes.Role;
};

export const RoleRow = ({ role }: Props) => {
  const deleteRole = useDeleteRole();
  const queryClient = useQueryClient();
  const [isDeletePreparing, setIsDeletePreparing] = useState(false);

  const handleDelete = async () => {
    if (isDeletePreparing) {
      return;
    }

    // Fetch fresh accounts at click time so the dialog never shows stale role holders.
    setIsDeletePreparing(true);
    let accountsData: Awaited<ReturnType<typeof getAccounts>> | undefined;
    let isError = false;
    try {
      accountsData = await queryClient.fetchQuery({ queryKey: ['accounts'], queryFn: getAccounts });
    } catch {
      isError = true;
    }
    setIsDeletePreparing(false);

    const title = `Delete role "${role.name}"?`;

    let description: string | undefined;
    if (isError || !accountsData) {
      description = 'Could not load affected accounts. Proceeding will still delete the role.';
    } else {
      const affected = accountsData.accounts.filter((account) =>
        (account.roleIds ?? []).includes(role._id)
      );
      if (affected.length === 0) {
        description = 'No accounts currently hold this role.';
      } else {
        const MAX_SHOWN = 5;
        const names = affected
          .slice(0, MAX_SHOWN)
          .map((account) => account.username)
          .join(', ');
        const rest = affected.length - MAX_SHOWN;
        const suffix = rest > 0 ? ` and ${rest} more` : '';
        description = `The following accounts will lose this role: ${names}${suffix}.`;
      }
    }

    NiceModal.show(ConfirmModal, {
      title,
      description,
      onSuccess: () => deleteRole.mutate({ id: role._id }),
    });
  };

  const isDeleteLoading = isDeletePreparing;

  let deleteTooltip = 'Delete';
  if (role.isSystem) {
    deleteTooltip = 'System role cannot be deleted';
  } else if (isDeleteLoading) {
    deleteTooltip = 'Loading affected accounts...';
  }

  return (
    <Table.Tr>
      <Table.Td>
        <Group gap="xs">
          <Text fw={500}>{role.name}</Text>
          {role.isSystem && (
            <Badge size="xs" color="gray" variant="light">
              system
            </Badge>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        {role.description ? (
          <Text size="sm" c="dimmed">
            {role.description}
          </Text>
        ) : (
          <Text ta="center" c="dimmed" size="sm">
            —
          </Text>
        )}
      </Table.Td>
      <RolePermissionsCell permissions={role.permissions} />
      <RolePageAccessCell pageAccess={role.pageAccess} />
      <Table.Td>
        {role.managerDepartmentAccess && role.managerDepartmentAccess.length > 0 ? (
          role.managerDepartmentAccess.length >= ALL_MANAGERS_COUNT ? (
            <Badge size="xs" variant="light" color="orange" radius="sm">
              All
            </Badge>
          ) : (
            <Group gap={4} wrap="wrap">
              {role.managerDepartmentAccess.map((department) => (
                <Badge key={department} size="xs" variant="light" color="orange" radius="sm">
                  {department.toUpperCase()}
                </Badge>
              ))}
            </Group>
          )
        ) : (
          <Text ta="center" c="dimmed" size="sm">
            —
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Group gap="xs" justify="right">
          <Tooltip label={role.isSystem ? 'System role cannot be edited' : 'Edit'}>
            <ActionIcon
              variant="filled"
              radius="md"
              disabled={role.isSystem}
              onClick={() => NiceModal.show(RoleModal, { role })}
              aria-label="Edit role"
            >
              <IconEdit color={COLORS.text} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={deleteTooltip}>
            <ActionIcon
              variant="filled"
              color="red"
              radius="md"
              disabled={role.isSystem}
              loading={isDeleteLoading}
              onClick={handleDelete}
              aria-label="Delete role"
            >
              <IconTrash color={COLORS.text} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
};
